import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Footer from './Footer';
import Logo from './Logo';

const Layout = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const headerClasses = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
    isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
  }`;

  const linkClasses = ({ isActive }: { isActive: boolean }) =>
    `header-link text-base font-medium transition-colors ${
      isActive ? 'active text-primary-dark' : 'text-neutral-700 hover:text-primary'
    }`;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/what-it-does', label: 'What It Does' },
    { path: '/subscription', label: 'Subscription' },
    { path: '/how-it-works', label: 'How It Works' },
    { path: '/history', label: 'History' },
    { path: '/news', label: 'News' },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <header className={headerClasses}>
        <div className="flex justify-between items-center pl-4 pr-4 md:pr-8 py-8 md:py-10">
          <NavLink to="/" className="flex items-center">
            <Logo />
          </NavLink>

          {/* Desktop navigation */}
          <nav className="hidden md:flex gap-8">
            {navLinks.map((link) => (
              <NavLink key={link.path} to={link.path} className={linkClasses}>
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-neutral-800"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile navigation */}
        {isMenuOpen && (
          <nav className="md:hidden bg-white py-4 px-4 shadow-lg animate-fade-in">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <NavLink key={link.path} to={link.path} className={linkClasses}>
                  {link.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main className="flex-grow pt-40 md:pt-48">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}

export default Layout;